import apiService from './ApiService';
import ParticularService from './ParticularService';

// Servicio para operaciones relacionadas con usuarios
class UsuarioService {
  
  // Login de usuario
  async login(email, password) {
    try {
      const loginData = {
        email: email,
        password: password
      };

      // Usar el método especializado para login que maneja automáticamente errores 415
      const response = await apiService.postLogin('/User/login', loginData);
      
      // Si hay token, configurarlo en el servicio API
      if (response.token) {
        apiService.setAuthToken(response.token);
        
        // Debug inmediato para verificar que se guardó
        apiService.debugTokenStatus();
      }
      
      return response;
      
    } catch (error) {
      console.error('%c❌ ERROR EN LOGIN', 'color: #ef4444; font-weight: bold;');
      console.error(`%c│ Error: ${error.message}`, 'color: #ef4444;');
      throw error;
    }
  }

  // Logout de usuario
  async logout() {
    try {
      // Limpiar el token del servicio API
      apiService.setAuthToken(null);
      
      // Opcional: llamar al endpoint de logout en el backend si existe
      // await apiService.post('/User/logout');
      
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Aún así limpiar el token localmente
      apiService.setAuthToken(null);
      throw error;
    }
  }

  async selectColegio(colegioId) {
    if (!colegioId) {
      throw new Error('Debes seleccionar un colegio.');
    }

    const response = await apiService.post('/User/seleccionar-colegio', { idColegio: colegioId });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos seleccionar el colegio.';
      throw new Error(msg);
    }

    return response;
  }

  async selectParticular(particularId) {
    if (!particularId) {
      throw new Error('Debes seleccionar un particular.');
    }

    const response = await apiService.post('/User/seleccionar-particular', {
      idProfesorParticular: particularId
    });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos seleccionar el particular.';
      throw new Error(msg);
    }

    const particulares = response?.particulares || response?.Particulares || [];
    const selectedParticular = Array.isArray(particulares)
      ? particulares.find((particular) => {
          const id = particular?.id || particular?.Id;
          return String(id || '') === String(particularId);
        })
      : null;
    if (selectedParticular) {
      ParticularService.syncActiveParticular(selectedParticular);
    }

    return response;
  }

  // Obtener perfil del usuario actual
  async getProfile() {
    try {
      const response = await apiService.get('/User/profile', apiService.getAuthHeaders());
      
      return response;
      
    } catch (error) {
      console.error('Error al obtener perfil:', error);
      throw error;
    }
  }

  // Validar token actual
  async validateToken() {
    try {
      const response = await apiService.get('/User/validate', apiService.getAuthHeaders());
      return response;
    } catch (error) {
      console.error('Token inválido:', error);
      throw error;
    }
  }

  // Actualizar contraseña de usuario
  async updatePassword(currentPassword, newPassword, repeatNewPassword) {
    try {
      const passwordData = {
        currentPassword: currentPassword,
        newPassword: newPassword,
        repeatNewPassword: repeatNewPassword
      };

      const response = await apiService.put('/User/update-password', passwordData);
      
      return response;
    } catch (error) {
      console.error('%c❌ ERROR AL ACTUALIZAR CONTRASEÑA:', 'color: #ef4444; font-weight: bold;', error);
      
      // Mejorar los mensajes de error paral usuario
      if (error.message.includes('401')) {
        throw new Error('La contraseña actual es incorrecta');
      } else if (error.message.includes('400')) {
        throw new Error('Los datos de la contraseña no son válidos');
      } else if (error.message.includes('422')) {
        throw new Error('La nueva contraseña no cumple con los requisitos de seguridad');
      } else {
        throw new Error('Error al actualizar la contraseña. Inténtalo nuevamente.');
      }
    }
  }

  async updatePasswordWithToken(currentPassword, newPassword, repeatNewPassword, token) {
    try {
      const passwordData = {
        currentPassword,
        newPassword,
        repeatNewPassword
      };

      return await apiService.request('/User/update-password', {
        method: 'PUT',
        body: passwordData,
        authTokenOverride: token
      });
    } catch (error) {
      console.error('%c❌ ERROR AL ACTUALIZAR CONTRASEÑA EN AULA:', 'color: #ef4444; font-weight: bold;', error);
      throw new Error(error.message || 'No pudimos actualizar la contraseña.');
    }
  }

  // Solicitar reset de contraseña por email
  async requestPasswordReset(email, captchaToken) {
    try {
      const response = await apiService.post('/User/password-reset/request', { email, captchaToken });
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos enviar el email de restauración.';
        throw new Error(msg);
      }
      return response;
    } catch (error) {
      console.error('❌ Error al solicitar reset de password:', error);
      throw new Error(error.message || 'No pudimos enviar el email de restauración.');
    }
  }

  // Validar token de reset
  async validatePasswordResetToken(token) {
    try {
      const response = await apiService.get(`/User/password-reset/validate?token=${encodeURIComponent(token)}`);
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'El token no es válido.';
        throw new Error(msg);
      }
      return response;
    } catch (error) {
      console.error('❌ Error al validar token de password:', error);
      throw new Error(error.message || 'El token no es válido.');
    }
  }

  // Confirmar reset de contraseña
  async confirmPasswordReset(token, newPassword, repeatNewPassword) {
    try {
      const response = await apiService.post('/User/password-reset/confirm', {
        token,
        newPassword,
        repeatNewPassword
      });
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar la contraseña.';
        throw new Error(msg);
      }
      return response;
    } catch (error) {
      console.error('❌ Error al confirmar reset de password:', error);
      throw new Error(error.message || 'No pudimos actualizar la contraseña.');
    }
  }

  async createAccessRequest(payload) {
    try {
      const response = await apiService.post('/User/access-request', payload);
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos enviar la solicitud.';
        throw new Error(msg);
      }
      return response;
    } catch (error) {
      console.error('❌ Error al enviar solicitud de acceso:', error);
      throw new Error(error.message || 'No pudimos enviar la solicitud.');
    }
  }

  async getAccessRequests(page = 1, pageSize = 50) {
    try {
      const response = await apiService.get(`/User/access-requests?page=${page}&pageSize=${pageSize}`);
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener las solicitudes.';
        throw new Error(msg);
      }
      return response;
    } catch (error) {
      console.error('❌ Error al obtener solicitudes de acceso:', error);
      throw new Error(error.message || 'No pudimos obtener las solicitudes.');
    }
  }

  async getAccessRequestById(id) {
    try {
      const response = await apiService.get(`/User/access-requests/${id}`);
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener la solicitud.';
        throw new Error(msg);
      }
      return response;
    } catch (error) {
      console.error('❌ Error al obtener solicitud:', error);
      throw new Error(error.message || 'No pudimos obtener la solicitud.');
    }
  }

  async getAccessRequestUnreadCount() {
    try {
      const response = await apiService.get('/User/access-requests/unread-count');
      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener el conteo.';
        throw new Error(msg);
      }
      return response;
    } catch (error) {
      console.error('❌ Error al obtener conteo de solicitudes:', error);
      throw new Error(error.message || 'No pudimos obtener el conteo.');
    }
  }

  async getProfesoresPorColegio(colegioId, options = {}) {
    if (!colegioId) {
      throw new Error('No hay un colegio activo seleccionado.');
    }

    try {
      const params = new URLSearchParams();
      if (options.start !== undefined && options.start !== null) params.append('start', options.start);
      if (options.end !== undefined && options.end !== null) params.append('end', options.end);
      const qs = params.toString();
      const response = await apiService.get(`/profesores/por-colegio/${colegioId}${qs ? `?${qs}` : ''}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener los profesores.';
        throw new Error(msg);
      }

      const profesores = response?.profesores || response?.Profesores || response?.data || response || [];
      const items = Array.isArray(profesores) ? profesores : [];
      const total = response?.total ?? response?.Total ?? items.length;
      if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
        return { items, total };
      }
      return items;
    } catch (error) {
      console.error('❌ Error al obtener profesores por colegio:', error);

      if (error.message?.includes('404')) {
        throw new Error('No se encontraron profesores para este colegio.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar los profesores de este colegio.');
    }
  }

  async updateProfesorActivo(profesorId, activo) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }

    try {
      const response = await apiService.put(`/profesores/${profesorId}/activo`, { activo });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado del profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del profesor:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el estado del profesor.');
    }
  }

  async updateAlumnoActivo(alumnoId, activo) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }

    try {
      const response = await apiService.put(`/alumnos/${alumnoId}/activo`, { activo });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado del alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del alumno:', error);

      if (error.message?.includes('404')) {
        throw new Error('El alumno no existe.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el estado del alumno.');
    }
  }

  async createProfesor(payload = {}) {
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para crear el profesor.');
    }

    try {
      const response = await apiService.post('/profesores', payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos crear el profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear profesor:', error);

      if (error.message?.includes('409')) {
        throw new Error('Ya existe un profesor con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos crear el profesor.');
    }
  }

  async updateProfesor(profesorId, payload = {}) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para actualizar el profesor.');
    }

    try {
      const response = await apiService.put(`/profesores/${profesorId}`, payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar profesor:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }
      if (error.message?.includes('409')) {
        throw new Error('Ya existe un profesor con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el profesor.');
    }
  }

  async getProfesorById(profesorId) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }

    try {
      const response = await apiService.get(`/profesores/${profesorId}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener el profesor.';
        throw new Error(msg);
      }

      const prof = response?.profesor || response?.Profesor || response;
      return prof;
    } catch (error) {
      console.error('❌ Error al obtener profesor por id:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar el profesor.');
    }
  }

  async getProfesorCursoMaterias(profesorId, idColegio) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para consultar las materias por curso.');
    }

    const response = await apiService.get(`/profesores/${profesorId}/curso-materias?idColegio=${encodeURIComponent(idColegio)}`);
    return {
      opciones: response?.opciones || response?.Opciones || [],
      asignadas: response?.asignadas || response?.Asignadas || []
    };
  }

  async assignProfesorCursoMaterias(profesorId, idColegio, idCursoMateriaIds = []) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para asociar materias por curso.');
    }

    const ids = Array.isArray(idCursoMateriaIds) ? idCursoMateriaIds.filter(Boolean) : [];
    const response = await apiService.post(`/profesores/${profesorId}/curso-materias/asociar`, {
      idColegio,
      idCursoMateriaIds: ids
    });

    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos asociar la materia al curso del profesor.';
      throw new Error(msg);
    }

    return response;
  }

  async removeProfesorCursoMaterias(profesorId, idColegio, idCursoMateriaIds = []) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para desasociar materias por curso.');
    }

    const ids = Array.isArray(idCursoMateriaIds) ? idCursoMateriaIds.filter(Boolean) : [];
    const response = await apiService.post(`/profesores/${profesorId}/curso-materias/desasociar`, {
      idColegio,
      idCursoMateriaIds: ids
    });

    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos desasociar la materia del curso del profesor.';
      throw new Error(msg);
    }

    return response;
  }

  async getAlumnoCursos(alumnoId, idColegio) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para consultar los cursos del alumno.');
    }

    const response = await apiService.get(`/alumnos/${alumnoId}/cursos?idColegio=${encodeURIComponent(idColegio)}`);
    return {
      opciones: response?.opciones || response?.Opciones || [],
      asignadas: response?.asignadas || response?.Asignadas || []
    };
  }

  async assignAlumnoCursos(alumnoId, idColegio, cursoIds = []) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para asociar cursos al alumno.');
    }

    const ids = Array.isArray(cursoIds) ? cursoIds.filter(Boolean) : [];
    const response = await apiService.post(`/alumnos/${alumnoId}/cursos/asociar`, {
      idColegio,
      cursoIds: ids
    });

    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos asociar el curso al alumno.';
      throw new Error(msg);
    }

    return response;
  }

  async removeAlumnoCursos(alumnoId, idColegio, cursoIds = []) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para desasociar cursos del alumno.');
    }

    const ids = Array.isArray(cursoIds) ? cursoIds.filter(Boolean) : [];
    const response = await apiService.post(`/alumnos/${alumnoId}/cursos/desasociar`, {
      idColegio,
      cursoIds: ids
    });

    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos desasociar el curso del alumno.';
      throw new Error(msg);
    }

    return response;
  }

  async getAlumnoFamiliares(alumnoId, idColegio) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para consultar los familiares del alumno.');
    }

    const response = await apiService.get(`/alumnos/${alumnoId}/familiares?idColegio=${encodeURIComponent(idColegio)}`);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos obtener los familiares.';
      throw new Error(msg);
    }

    return response?.familiares || response?.Familiares || [];
  }

  async createAlumnoFamiliar(alumnoId, payload = {}) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para crear el familiar.');
    }

    const response = await apiService.post(`/alumnos/${alumnoId}/familiares`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos crear el familiar.';
      throw new Error(msg);
    }

    return response;
  }

  async updateAlumnoFamiliar(alumnoId, familiarId, payload = {}) {
    if (!alumnoId || !familiarId) {
      throw new Error('Faltan datos para actualizar el familiar.');
    }
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para actualizar el familiar.');
    }

    const response = await apiService.put(`/alumnos/${alumnoId}/familiares/${familiarId}`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos actualizar el familiar.';
      throw new Error(msg);
    }

    return response;
  }

  async removeAlumnoFamiliar(alumnoId, familiarId, idColegio) {
    if (!alumnoId || !familiarId || !idColegio) {
      throw new Error('Faltan datos para quitar el familiar.');
    }

    const response = await apiService.request(
      `/alumnos/${alumnoId}/familiares/${familiarId}?idColegio=${encodeURIComponent(idColegio)}`,
      { method: 'DELETE' }
    );
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos quitar el familiar.';
      throw new Error(msg);
    }

    return response;
  }

  async resetAlumnoFamiliarPassword(alumnoId, familiarId, idColegio) {
    if (!alumnoId || !familiarId || !idColegio) {
      throw new Error('Faltan datos para resetear la contraseña del familiar.');
    }

    const response = await apiService.put(
      `/alumnos/${alumnoId}/familiares/${familiarId}/reset-password?idColegio=${encodeURIComponent(idColegio)}`,
      {}
    );
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos resetear la contraseña del familiar.';
      throw new Error(msg);
    }

    return response;
  }

  async createAlumno(payload = {}) {
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para crear el alumno.');
    }

    try {
      const response = await apiService.post('/alumnos', payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos crear el alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear alumno:', error);

      if (error.message?.includes('409')) {
        throw new Error('Ya existe un alumno con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos crear el alumno.');
    }
  }

  async updateAlumno(alumnoId, payload = {}) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para actualizar el alumno.');
    }

    try {
      const response = await apiService.put(`/alumnos/${alumnoId}`, payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar alumno:', error);

      if (error.message?.includes('404')) {
        throw new Error('El alumno no existe.');
      }
      if (error.message?.includes('409')) {
        throw new Error('Ya existe un alumno con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el alumno.');
    }
  }

  async getAlumnosPorColegio(colegioId, options = {}) {
    if (!colegioId) {
      throw new Error('No hay un colegio activo seleccionado.');
    }

    try {
      const params = new URLSearchParams();
      if (options.start !== undefined && options.start !== null) params.append('start', options.start);
      if (options.end !== undefined && options.end !== null) params.append('end', options.end);
      const qs = params.toString();
      const response = await apiService.get(`/alumnos/por-colegio/${colegioId}${qs ? `?${qs}` : ''}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener los alumnos.';
        throw new Error(msg);
      }

      const alumnos = response?.alumnos || response?.Alumnos || response?.data || response || [];
      const items = Array.isArray(alumnos) ? alumnos : [];
      const total = response?.total ?? response?.Total ?? items.length;
      if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
        return { items, total };
      }
      return items;
    } catch (error) {
      console.error('❌ Error al obtener alumnos por colegio:', error);

      if (error.message?.includes('404')) {
        throw new Error('No se encontraron alumnos para este colegio.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar los alumnos de este colegio.');
    }
  }

  async validateProfesorPorEmail(email, idColegio) {
    if (!email) {
      throw new Error('Debes ingresar un email para validar al profesor.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para validar al profesor.');
    }

    try {
      const response = await apiService.post('/profesores/validar-por-mail', {
        email,
        idColegio
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos validar el profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al validar profesor por email:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos validar el profesor.');
    }
  }

  async validateAlumnoPorEmail(email, idColegio) {
    if (!email) {
      throw new Error('Debes ingresar un email para validar al alumno.');
    }
    if (!idColegio) {
      throw new Error('Debes seleccionar un colegio para validar al alumno.');
    }

    try {
      const response = await apiService.post('/alumnos/validar-por-mail', {
        email,
        idColegio
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos validar el alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al validar alumno por email:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos validar el alumno.');
    }
  }

  async desasociarProfesorDeColegio(profesorId, idColegio) {
    if (!profesorId || !idColegio) {
      throw new Error('Faltan datos para desasociar al profesor del colegio.');
    }

    try {
      const response = await apiService.request('/profesores/asociacion', {
        method: 'DELETE',
        body: { profesorId, idColegio },
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos desasociar al profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al desasociar profesor:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos desasociar al profesor.');
    }
  }

  async desasociarAlumnoDeColegio(alumnoId, idColegio) {
    if (!alumnoId || !idColegio) {
      throw new Error('Faltan datos para desasociar al alumno del colegio.');
    }

    try {
      const response = await apiService.request('/alumnos/asociacion', {
        method: 'DELETE',
        body: { alumnoId, idColegio },
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos desasociar al alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al desasociar alumno:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos desasociar al alumno.');
    }
  }

  // ---------- Admin Área ----------

  async getAdminAreaUsersPorColegio(colegioId, options = {}) {
    if (!colegioId) {
      throw new Error('No hay un colegio activo seleccionado.');
    }

    try {
      const params = new URLSearchParams();
      if (options.start !== undefined && options.start !== null) params.append('start', options.start);
      if (options.end !== undefined && options.end !== null) params.append('end', options.end);
      const qs = params.toString();
      const response = await apiService.get(`/adminareausers/por-colegio/${colegioId}${qs ? `?${qs}` : ''}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener los admins de área.';
        throw new Error(msg);
      }

      const usuarios = response?.usuarios || response?.Usuarios || response?.data || response || [];
      const items = Array.isArray(usuarios) ? usuarios : [];
      const total = response?.total ?? response?.Total ?? items.length;
      if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
        return { items, total };
      }
      return items;
    } catch (error) {
      console.error('❌ Error al obtener admins de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('No se encontraron admins de área para este colegio.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar los admins de área.');
    }
  }

  async createAdminAreaUser(payload = {}) {
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para crear el admin de área.');
    }

    try {
      const response = await apiService.post('/adminareausers', payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos crear el admin de área.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear admin de área:', error);

      if (error.message?.includes('409')) {
        throw new Error('Ya existe un admin de área con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos crear el admin de área.');
    }
  }

  async updateAdminAreaUser(adminAreaUserId, payload = {}) {
    if (!adminAreaUserId) {
      throw new Error('No se encontró el identificador del admin de área.');
    }
    if (!payload.idColegio) {
      throw new Error('Debes seleccionar un colegio para actualizar el admin de área.');
    }

    try {
      const response = await apiService.put(`/adminareausers/${adminAreaUserId}`, payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el admin de área.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar admin de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin de área no existe.');
      }
      if (error.message?.includes('409')) {
        throw new Error('Ya existe un admin de área con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el admin de área.');
    }
  }

  async updateAdminAreaActivo(adminAreaUserId, activo) {
    if (!adminAreaUserId) {
      throw new Error('No se encontró el identificador del admin de área.');
    }

    try {
      const response = await apiService.put(`/adminareausers/${adminAreaUserId}/activo`, { activo });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado del admin de área.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del admin de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin de área no existe.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el estado del admin de área.');
    }
  }

  async resetAdminAreaPassword(adminAreaUserId) {
    if (!adminAreaUserId) {
      throw new Error('No se encontró el identificador del admin de área.');
    }

    try {
      const response = await apiService.put(`/adminareausers/${adminAreaUserId}/reset-password`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos resetear la contraseña.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al resetear password de admin de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin de área no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos resetear la contraseña.');
    }
  }

  async resetProfesorPassword(profesorId) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }

    try {
      const response = await apiService.put(`/profesores/${profesorId}/reset-password`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos resetear la contraseña.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al resetear password de profesor:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos resetear la contraseña.');
    }
  }

  async resetAlumnoPassword(alumnoId) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }

    try {
      const response = await apiService.put(`/alumnos/${alumnoId}/reset-password`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos resetear la contraseña.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al resetear password de alumno:', error);

      if (error.message?.includes('404')) {
        throw new Error('El alumno no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos resetear la contraseña.');
    }
  }
}

// Crear instancia singleton
const usuarioService = new UsuarioService();

export default usuarioService;
